import axios from 'axios';
import { createAuthHeaders } from '../api-client';

const API_URL = import.meta.env.VITE_API_URL;

export interface CreditBalanceResponse {
  balance: number;
}

export type PlanType = 'free' | 'standard' | 'pro' | 'enterprise';

/** Which wall (if any) an out-of-credits free user has hit — drives drawer + modal copy. */
export type FreeCreditWall = 'none' | 'today' | 'monthly' | 'lifetime';

/**
 * Free-tier daily-credit state for standalone free workspaces (Lovable-style model).
 * Present only when the workspace is on the daily model; null/undefined for paid/embed.
 */
export interface FreeDailyCredits {
  isFreeDaily: boolean;
  dailyGrant: number;                 // credits granted per day (e.g. 10)
  monthlyCap: number;                 // max granted per calendar month (e.g. 50)
  monthGranted: number;               // granted so far this month (0..monthlyCap)
  lifetimeCap: number;                // max granted ever (e.g. 100) — the permanent wall
  lifetimeGranted: number;            // granted ever (0..lifetimeCap)
  monthResetAt: string | null;        // when the monthly cap resets (ISO) — the 1st
  dailyGrantAt?: string;              // next daily-grant instant (ISO); rendered in the viewer's tz
  wall: FreeCreditWall;               // server-computed: none | today | monthly | lifetime
}

export interface CreditDetailsResponse {
  balance: number;                    // Total (subscription + top-up)
  subscriptionBalance: number;        // Subscription credits (resets on billing cycle)
  topupBalance: number;               // Top-up credits (never resets)
  allocated: number;
  totalUsed: number;
  currentCycleUsed: number;
  lastResetAt: string | null;
  nextResetAt: string | null;
  planType: PlanType;
  freeDaily?: FreeDailyCredits;       // free-tier daily model state (standalone free only)
}

export interface ModelUsageItem {
  model: string;
  credits: number;
  costUsd: number;
  requests: number;
}

export interface ProjectUsageItem {
  projectId: string;
  projectName: string;
  credits: number;
  costUsd: number;
  requests: number;
}

export const creditsApi = {
  /**
   * Get current credit balance for workspace
   */
  async getBalance(token: string): Promise<number> {
    const response = await axios.get<CreditBalanceResponse>(
      `${API_URL}/api/credits/balance`,
      { headers: createAuthHeaders(token) },
    );
    return response.data.balance;
  },

  /**
   * Get full credit details for workspace
   */
  async getDetails(token: string): Promise<CreditDetailsResponse> {
    const response = await axios.get<CreditDetailsResponse>(
      `${API_URL}/api/credits/details`,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Get credit usage broken down by model for current billing cycle
   */
  async getModelUsage(token: string): Promise<ModelUsageItem[]> {
    const response = await axios.get<ModelUsageItem[]>(
      `${API_URL}/api/credits/model-usage`,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Get credit usage broken down by project for current billing cycle
   */
  async getProjectUsage(token: string): Promise<ProjectUsageItem[]> {
    const response = await axios.get<ProjectUsageItem[]>(
      `${API_URL}/api/credits/project-usage`,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },
};
